export type ReportMessageLanguage = 'en' | 'ru';

type PluralCategory = 'one' | 'few' | 'many' | 'other';
type ReportMessageTemplate = string | Partial<Record<PluralCategory, string>>;
type ReportMessageParams = Record<string, string | number>;

const ENGLISH: Record<string, ReportMessageTemplate> = {
	// CONTRACT: thin.counted.* and thin.window.* are the backend's too, word for
	// word in every language (reports/report_messages.py): a thin section and a
	// thin chart describe the same period in the same words.
	'thin.counted.some': {
		one: 'only {count} mention',
		other: 'only {count} mentions'
	},
	'thin.window.last_7_days': 'in the last 7 days',
	'thin.window.last_30_days': 'in the last 30 days',
	'thin.window.last_3_months': 'in the last 3 months',
	'thin.window.last_6_months': 'in the last 6 months',
	'thin.window.last_year': 'in the last year',
	'thin.window.all_time': 'so far',
	'thin.window.other': 'in this period',
	'thin.window.custom_range': 'between {start} and {end}',
	'thin.window.custom_day': 'on {day}',
	'thin.slide.none':
		'No mentions {when} — nothing to read {reads} from yet. ' +
		'This section fills in once there are {floor}.',
	'thin.slide.some':
		'{counted} {when} — too few to read {reads} reliably. ' +
		'This section fills in once there are {floor}.',

	'section.reads.executive_summary': 'the headline KPIs',
	'section.reads.volume_of_mentions_reach': 'mentions and reach',
	'section.reads.sentiment_over_time': 'sentiment over time',
	'section.reads.emotion_over_time': 'emotion over time',
	'section.reads.sentiment_by_media_type': 'sentiment by media type',
	'section.reads.emotion_analysis': 'the emotion breakdown',
	'section.reads.top_influencers_by_voice_share': 'voice share',
	'section.reads.top_sites_by_visits': 'the top sites',
	'section.reads.influencers_by_media_type': 'influencers by media type',
	'section.reads.best_time_to_post': 'the best time to post',
	'section.reads.website_traffic_demographics': 'audience demographics',
	'section.reads.top_countries': 'top countries',
	'section.reads.trending_hashtags': 'trending hashtags',
	'section.reads.trending_conversations': 'trending conversations',
	'section.reads.mentions_by_media_type': 'mentions by media type',
	'section.reads.languages': 'the language mix',

	'section.measure.executive_summary': 'Headline KPIs',
	'section.measure.volume_of_mentions_reach': 'Mentions & reach',
	'section.measure.sentiment_over_time': 'Sentiment over time',
	'section.measure.emotion_over_time': 'Emotion over time',
	'section.measure.sentiment_by_media_type': 'Sentiment by media type',
	'section.measure.emotion_analysis': 'Emotion breakdown',
	'section.measure.top_influencers_by_voice_share':
		'Voice share by influencer',
	'section.measure.top_sites_by_visits': 'Mentioning sites by visits',
	'section.measure.influencers_by_media_type': 'Influencers by media type',
	'section.measure.best_time_to_post': 'Engagement by day and hour',
	'section.measure.website_traffic_demographics':
		'Website audience demographics',
	'section.measure.top_countries': 'Mentions by country',
	'section.measure.trending_hashtags': 'Hashtags in mentions',
	'section.measure.trending_conversations': 'Recurring phrases in mentions',
	'section.measure.mentions_by_media_type': 'Mentions by media type',
	'section.measure.languages': 'Mentions by language',

	// The sections kept under the brand: what their source line says they read.
	'block.measure.alerts_fired': 'Alerts fired',
	'block.measure.review_ratings':
		'Average rating of collected reviews dated in the period',
	'block.measure.spikes_and_why': 'Spikes and what carried them',
	'block.measure.content_gaps': 'Content gaps',
	'block.measure.mentions_over_time': 'Mentions over time',
	'block.measure.reach_over_time': 'Reach over time',
	'block.measure.engagement_over_time': 'Engagement over time',
	'block.measure.engagement_rate_over_time': 'Engagement rate over time',
	'block.measure.sentiment_over_time': 'Sentiment over time',
	'block.measure.emotion_over_time': 'Emotion over time',
	'block.measure.mentions_by_platform': 'Mentions by platform',
	'block.measure.sentiment_by_platform': 'Sentiment by platform',
	'block.measure.authors_by_platform': 'Authors by platform',
	'block.measure.emotion_distribution': 'Emotion breakdown',
	'block.measure.mentions_by_language': 'Languages',
	'block.measure.top_countries': 'Top countries',
	'block.measure.top_hashtags': 'Trending hashtags',
	'block.measure.trending_conversations': 'Trending conversations',
	'block.measure.best_time_to_post': 'Best time to post',
	'block.measure.top_influencers': 'Top voices',
	'block.measure.creator_shortlist': 'Top creators by mentions',
	'block.measure.top_sites': 'Top sites',
	'block.measure.kpi_totals': 'Headline numbers',
	'block.measure.top_mentions': 'Top mentions by engagement',
	'block.measure.what_people_love': 'Top positive mentions by engagement',
	'block.measure.needs_attention': 'Top negative mentions by engagement',
	'block.measure.share_of_voice': 'Share of voice',
	'block.measure.sentiment_totals': 'Mentions by sentiment',
	'block.measure.sentiment_shares': 'Share of mentions by sentiment',
	'block.measure.competitor_briefing':
		'Mentions, share of voice, sentiment and reach',

	'source.measure.mentions': 'Mentions',
	'source.section.brand_only':
		'This section is drawn for a brand only, not for a competitor project or an industry.',
	'source.prefix': 'Source: {module}',
	'source.module.brand': 'Brand',
	'source.module.brands': 'Brands',
	'source.module.competitor': 'competitor',
	'source.module.competitors': 'competitors',
	'source.module.industry': 'industry',
	'source.module.industries': 'industries',
	'source.module.notifications': 'Notifications',
	'source.module.reviews': 'Reviews',
	'source.module.and': ' & ',
	'source.origin.competitors': 'Competitors',
	'source.origin.industry': 'Industry',
	'source.names.versus': ' vs ',
	'source.window.versus': '{current} vs {previous}',
	'source.exact': 'exact match',
	'source.grouping.week': 'weekly',
	'source.grouping.month': 'monthly',
	'source.filter.platforms': 'Platforms',
	'source.filter.sentiment': 'Sentiment',
	'source.filter.languages': 'Languages',
	'source.sentiment.positive': 'Positive',
	'source.sentiment.neutral': 'Neutral',
	'source.sentiment.negative': 'Negative',
	'source.detail.module': 'Module: Social Listening · {module}',
	'source.detail.brand': 'Brand: {label}',
	'source.detail.competitor': 'Competitor: {label}',
	'source.detail.industry': 'Industry: {label}',
	'source.detail.competitor_project': 'Competitor project: {label}',
	'source.detail.compared': 'Compared: {entities}',
	'source.detail.entity': '{label} ({kind})',
	'source.kind.brand': 'brand',
	'source.kind.competitor': 'competitor',
	'source.kind.industry': 'industry',
	'source.detail.measure': 'Measure: {measure}',
	'source.detail.filter': '{name}: {value} only',
	'source.detail.grouped': 'Grouped: {grouping} totals',
	'source.detail.match': 'Match type: exact — whole-word keyword hits only',
	'source.detail.range': 'Date range: {range}',
	'source.detail.compared_with':
		'Compared with: {range}, the same number of days just before',
	'source.detail.live':
		'Numbers are read live each time the report is opened, so they ' +
		'can change as new mentions are collected.',
	'source.detail.live.notifications':
		'Read live each time the report is opened, so an alert that fires ' +
		'later in the period is listed too.',
	'source.detail.live.reviews':
		'Read live each time the report is opened, so the figures change as ' +
		'new reviews are collected.',
	'source.detail.window': '{start} – {end} UTC',
	'source.detail.all_time': 'all time, up to {end} UTC',
	'window.all_time': 'All time',

	'empty.no_data': 'No data to display at the moment',
	'empty.nothing': 'Nothing to show for this period.',
	'empty.cannot_draw': 'This block cannot be drawn yet.',
	'empty.quote_gone': 'This mention is not available.',

	'creators.mentions': 'Mentions',
	'creators.followers': 'Profile followers',
	'creators.followers.hint':
		"From the creator's profile, not a figure for this period.",
	'creators.positive': 'Positive',
	'creators.negative': 'Negative',

	'kpi.mentions': 'Mentions',
	'kpi.likes': 'Likes',
	'kpi.positive_mentions': 'Positive Mentions',
	'kpi.social_media_reach': 'Social Media Reach',
	'kpi.web_mentions': 'Web Mentions',
	'kpi.reach_total': 'Reach (total)',
	'kpi.negative_mentions': 'Negative Mentions',
	'kpi.social_mentions': 'Social Mentions',
	'kpi.social_interactions': 'Social Interactions',
	'kpi.web_sites': 'Sites Mentioning',
	'kpi.vs_previous': 'vs. prev period',
	'kpi.share': 'share {value}%',

	'cover.eyebrow': 'Social Listening Report',
	'share.live': 'Live data',

	// The editor's flag under a text whose number the live data has moved on
	// from (report-figure-flags). Never printed in the PDF or a share link.
	'flag.now': 'This number is now {now}.',
	'flag.origin':
		'The text says {quoted}. The live data has changed since it was written.',
	'flag.update': 'Update',
	'flag.keep': 'Keep',
	'flag.update_label': 'Update {quoted} to {now}',
	'flag.keep_label': 'Keep {quoted}',

	// The drawings of the sections kept under the brand.
	'alerts.fires': {
		one: '{count} fire',
		other: '{count} fires'
	},
	'reviews.count': {
		one: '{count} review',
		other: '{count} reviews'
	},
	'reviews.distribution': 'Rated reviews by stars'
};

const RUSSIAN: Record<string, ReportMessageTemplate> = {
	'thin.counted.some': {
		one: 'всего {count} упоминание',
		few: 'всего {count} упоминания',
		many: 'всего {count} упоминаний'
	},
	'thin.window.last_7_days': 'за последние 7 дней',
	'thin.window.last_30_days': 'за последние 30 дней',
	'thin.window.last_3_months': 'за последние 3 месяца',
	'thin.window.last_6_months': 'за последние 6 месяцев',
	'thin.window.last_year': 'за последний год',
	'thin.window.all_time': 'за всё время',
	'thin.window.other': 'за этот период',
	'thin.window.custom_range': 'с {start} по {end}',
	'thin.window.custom_day': 'за {day}',
	'thin.slide.none':
		'Ни одного упоминания {when} — пока не из чего оценить {reads}. ' +
		'Раздел заполнится, когда упоминаний станет {floor}.',
	'thin.slide.some':
		'{counted} {when} — слишком мало, чтобы надёжно оценить {reads}. ' +
		'Раздел заполнится, когда упоминаний станет {floor}.',

	'section.reads.executive_summary': 'ключевые показатели',
	'section.reads.volume_of_mentions_reach': 'упоминания и охват',
	'section.reads.sentiment_over_time': 'динамику тональности',
	'section.reads.emotion_over_time': 'динамику эмоций',
	'section.reads.sentiment_by_media_type': 'тональность по типам медиа',
	'section.reads.emotion_analysis': 'разбивку по эмоциям',
	'section.reads.top_influencers_by_voice_share': 'долю голоса',
	'section.reads.top_sites_by_visits': 'топ сайтов',
	'section.reads.influencers_by_media_type': 'инфлюенсеров по типам медиа',
	'section.reads.best_time_to_post': 'лучшее время для публикаций',
	'section.reads.website_traffic_demographics': 'демографию аудитории',
	'section.reads.top_countries': 'топ стран',
	'section.reads.trending_hashtags': 'популярные хештеги',
	'section.reads.trending_conversations': 'популярные темы обсуждений',
	'section.reads.mentions_by_media_type': 'упоминания по типам медиа',
	'section.reads.languages': 'языковой состав',

	'section.measure.executive_summary': 'Ключевые показатели',
	'section.measure.volume_of_mentions_reach': 'Упоминания и охват',
	'section.measure.sentiment_over_time': 'Тональность во времени',
	'section.measure.emotion_over_time': 'Эмоции во времени',
	'section.measure.sentiment_by_media_type': 'Тональность по типам медиа',
	'section.measure.emotion_analysis': 'Разбивка по эмоциям',
	'section.measure.top_influencers_by_voice_share':
		'Доля голоса по инфлюенсерам',
	'section.measure.top_sites_by_visits':
		'Сайты с упоминаниями по посещаемости',
	'section.measure.influencers_by_media_type': 'Инфлюенсеры по типам медиа',
	'section.measure.best_time_to_post': 'Вовлечённость по дням и часам',
	'section.measure.website_traffic_demographics':
		'Демография аудитории сайтов',
	'section.measure.top_countries': 'Упоминания по странам',
	'section.measure.trending_hashtags': 'Хештеги в упоминаниях',
	'section.measure.trending_conversations':
		'Повторяющиеся фразы в упоминаниях',
	'section.measure.mentions_by_media_type': 'Упоминания по типам медиа',
	'section.measure.languages': 'Упоминания по языкам',

	'block.measure.alerts_fired': 'Сработавшие оповещения',
	'block.measure.review_ratings':
		'Средняя оценка собранных отзывов за период',
	'block.measure.spikes_and_why': 'Всплески и что их определило',
	'block.measure.content_gaps': 'Пробелы в контенте',
	'block.measure.mentions_over_time': 'Упоминания во времени',
	'block.measure.reach_over_time': 'Охват во времени',
	'block.measure.engagement_over_time': 'Вовлечённость во времени',
	'block.measure.engagement_rate_over_time':
		'Коэффициент вовлечённости во времени',
	'block.measure.sentiment_over_time': 'Тональность во времени',
	'block.measure.emotion_over_time': 'Эмоции во времени',
	'block.measure.mentions_by_platform': 'Упоминания по платформам',
	'block.measure.sentiment_by_platform': 'Тональность по платформам',
	'block.measure.authors_by_platform': 'Авторы по платформам',
	'block.measure.emotion_distribution': 'Разбивка по эмоциям',
	'block.measure.mentions_by_language': 'Языки',
	'block.measure.top_countries': 'Топ стран',
	'block.measure.top_hashtags': 'Популярные хештеги',
	'block.measure.trending_conversations': 'Популярные темы обсуждений',
	'block.measure.best_time_to_post': 'Лучшее время для публикаций',
	'block.measure.top_influencers': 'Топ авторов',
	'block.measure.creator_shortlist': 'Топ авторов по числу упоминаний',
	'block.measure.top_sites': 'Топ сайтов',
	'block.measure.kpi_totals': 'Ключевые цифры',
	'block.measure.top_mentions': 'Топ упоминаний по вовлечённости',
	'block.measure.what_people_love':
		'Топ позитивных упоминаний по вовлечённости',
	'block.measure.needs_attention':
		'Топ негативных упоминаний по вовлечённости',
	'block.measure.share_of_voice': 'Доля голоса',
	'block.measure.sentiment_totals': 'Упоминания по тональности',
	'block.measure.sentiment_shares': 'Доли упоминаний по тональности',
	'block.measure.competitor_briefing':
		'Упоминания, доля голоса, тональность и охват',

	'source.measure.mentions': 'Упоминания',
	'source.section.brand_only':
		'Этот раздел строится только для бренда, а не для проекта конкурентов или отрасли.',
	'source.prefix': 'Источник: {module}',
	'source.module.brand': 'Бренд',
	'source.module.brands': 'Бренды',
	'source.module.competitor': 'конкурент',
	'source.module.competitors': 'конкуренты',
	'source.module.industry': 'отрасль',
	'source.module.industries': 'отрасли',
	'source.module.notifications': 'Уведомления',
	'source.module.reviews': 'Отзывы',
	'source.module.and': ' и ',
	'source.origin.competitors': 'Конкуренты',
	'source.origin.industry': 'Отрасль',
	'source.names.versus': ' vs ',
	'source.window.versus': '{current} в сравнении с {previous}',
	'source.exact': 'точное совпадение',
	'source.grouping.week': 'по неделям',
	'source.grouping.month': 'по месяцам',
	'source.filter.platforms': 'Платформы',
	'source.filter.sentiment': 'Тональность',
	'source.filter.languages': 'Языки',
	'source.sentiment.positive': 'Позитивная',
	'source.sentiment.neutral': 'Нейтральная',
	'source.sentiment.negative': 'Негативная',
	'source.detail.module': 'Модуль: Social Listening · {module}',
	'source.detail.brand': 'Бренд: {label}',
	'source.detail.competitor': 'Конкурент: {label}',
	'source.detail.industry': 'Отрасль: {label}',
	'source.detail.competitor_project': 'Проект конкурентов: {label}',
	'source.detail.compared': 'Сравнение: {entities}',
	'source.detail.entity': '{label} ({kind})',
	'source.kind.brand': 'бренд',
	'source.kind.competitor': 'конкурент',
	'source.kind.industry': 'отрасль',
	'source.detail.measure': 'Показатель: {measure}',
	'source.detail.filter': '{name}: только {value}',
	'source.detail.grouped': 'Группировка: итоги {grouping}',
	'source.detail.match':
		'Тип совпадения: точное — только совпадения ключевых слов целиком',
	'source.detail.range': 'Период: {range}',
	'source.detail.compared_with':
		'В сравнении с: {range}, столько же дней непосредственно перед этим',
	'source.detail.live':
		'Цифры считываются заново при каждом открытии отчёта, поэтому ' +
		'могут меняться по мере сбора новых упоминаний.',
	'source.detail.live.notifications':
		'Считываются заново при каждом открытии отчёта, поэтому оповещение, ' +
		'сработавшее позже в этом периоде, тоже появится в списке.',
	'source.detail.live.reviews':
		'Считываются заново при каждом открытии отчёта, поэтому цифры ' +
		'меняются по мере сбора новых отзывов.',
	'source.detail.window': '{start} – {end} UTC',
	'source.detail.all_time': 'за всё время, до {end} UTC',
	'window.all_time': 'За всё время',

	'empty.no_data': 'Сейчас нет данных для отображения',
	'empty.nothing': 'За этот период показать нечего.',
	'empty.cannot_draw': 'Этот блок пока нельзя построить.',
	'empty.quote_gone': 'Это упоминание недоступно.',

	'creators.mentions': 'Упоминания',
	'creators.followers': 'Подписчики профиля',
	'creators.followers.hint':
		'Из профиля автора, а не показатель за этот период.',
	'creators.positive': 'Позитивные',
	'creators.negative': 'Негативные',

	'kpi.mentions': 'Упоминания',
	'kpi.likes': 'Лайки',
	'kpi.positive_mentions': 'Позитивные упоминания',
	'kpi.social_media_reach': 'Охват в соцсетях',
	'kpi.web_mentions': 'Упоминания в вебе',
	'kpi.reach_total': 'Охват (всего)',
	'kpi.negative_mentions': 'Негативные упоминания',
	'kpi.social_mentions': 'Упоминания в соцсетях',
	'kpi.social_interactions': 'Взаимодействия в соцсетях',
	'kpi.web_sites': 'Сайты с упоминаниями',
	'kpi.vs_previous': 'к пред. периоду',
	'kpi.share': 'доля {value}%',

	'cover.eyebrow': 'Отчёт Social Listening',
	'share.live': 'Данные в реальном времени',

	'flag.now': 'Это число теперь {now}.',
	'flag.origin':
		'В тексте — {quoted}. Данные изменились с тех пор, как он был написан.',
	'flag.update': 'Обновить',
	'flag.keep': 'Оставить',
	'flag.update_label': 'Заменить {quoted} на {now}',
	'flag.keep_label': 'Оставить {quoted}',

	'alerts.fires': {
		one: '{count} срабатывание',
		few: '{count} срабатывания',
		many: '{count} срабатываний'
	},
	'reviews.count': {
		one: '{count} отзыв',
		few: '{count} отзыва',
		many: '{count} отзывов'
	},
	'reviews.distribution': 'Оценённые отзывы по звёздам'
};

const MESSAGES: Record<
	ReportMessageLanguage,
	Record<string, ReportMessageTemplate>
> = {
	en: ENGLISH,
	ru: RUSSIAN
};

const PLURAL_RULES: Record<
	ReportMessageLanguage,
	(count: number) => PluralCategory
> = {
	en: count => (count === 1 ? 'one' : 'other'),
	ru: count => {
		if (count % 10 === 1 && count % 100 !== 11) return 'one';
		if (
			count % 10 >= 2 &&
			count % 10 <= 4 &&
			!(count % 100 >= 12 && count % 100 <= 14)
		) {
			return 'few';
		}
		return 'many';
	}
};

const DATE_LOCALES: Record<ReportMessageLanguage, string> = {
	en: 'en-US',
	ru: 'ru'
};

export function reportMessageLanguage(
	language: string | null | undefined
): ReportMessageLanguage {
	const code = (language ?? '')
		.trim()
		.toLowerCase()
		.replace('_', '-')
		.split('-')[0];
	return code in MESSAGES ? (code as ReportMessageLanguage) : 'en';
}

export function hasReportMessage(key: string): boolean {
	return key in ENGLISH;
}

export function reportMessage(
	language: string | null | undefined,
	key: string,
	params: ReportMessageParams = {}
): string {
	let writtenIn = reportMessageLanguage(language);
	let template = MESSAGES[writtenIn][key];
	if (template === undefined) {
		writtenIn = 'en';
		template = ENGLISH[key] ?? key;
	}
	const text =
		typeof template === 'string'
			? template
			: template[PLURAL_RULES[writtenIn](Number(params['count']))] ?? '';
	return text.replace(/\{(\w+)\}/g, (hole, name: string) =>
		name in params ? String(params[name]) : hole
	);
}

export function reportDateLocale(language: string | null | undefined): string {
	return DATE_LOCALES[reportMessageLanguage(language)];
}

// TRAP: not a capitalize(), which would lower everything after the first
// letter — "Sam's Club" included.
export function sentenceStart(phrase: string): string {
	return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}
